import {Box, Flex, Icon, IconWithText, Image, Info, Tag, Text, Theme as ThemeWithoutType, Title} from 'exsportia-components'
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useQuery} from 'react-query'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'

import {useUpdatePosTerminalProcess} from '../../api/payments/update-pos-terminal-process.mutation'
import {ProviderStatusQueries} from '../../api/provider-status'
import {getOpnChargeState, isOpnChargeGated} from '../../api/provider-status/helpers'
import {getVenueLogoUrl} from '../../api/venue/venue-logo'
import {CheckoutGateNotice} from '../../components/checkout-gate-notice/checkout-gate-notice'
import {POS_TERMINAL_STATUSES} from '../../constants/payments'
import {TranslatorScope} from '../../locale/types'
import {useBookingLoader} from '../../services/booking/hooks'
import {PaymentService} from '../../services/payments'
import {resolveGateProvider} from '../../services/payments/helpers/checkout-gate'
import {useHandleDraftEntityStatusChange} from '../../services/payments/hooks/handle-draft-entity-status-change.hook'
import {PaymentProviderProvider, usePaymentProvider} from '../../services/payments/hooks/payment-provider-form.provider'
import {VenueService} from '../../services/venue'
import {ThemeType} from '../../theme/types'
import {Button} from '../../ui/components/button/button'
import {GradientLoader} from '../../ui/components/gradient-loader/gradient-loader'
import {CHECKIN_URL} from '../../utils/environment'
import {db} from '../../utils/firebase'
import {useLocale, useLuxon} from '../../utils/i18n'
import {setPosPaymentInfoToSession} from '../../utils/local-storage'
import {isNil, isNotNilAndNotEmpty} from '../../utils/logic'
import {useTimeout} from '../../utils/use-idle-timer'

export type PaymentPageQueryDataType = {withQR: string | null;entity: string | null; entityId: string | null; posTerminalProcessId: string | null; paymentMethod: string | null}
export const extractDataFromQuery = (searchParams:URLSearchParams):PaymentPageQueryDataType => ({
  entity: searchParams.get('entity'),
  entityId: searchParams.get('entityId'),
  withQR: searchParams.get('withQR'),
  posTerminalProcessId: searchParams.get('posTerminalProcessId'),
  paymentMethod: searchParams.get('paymentMethod'),
})

export const PaymentPageComponent: FC = () => {
  const {l} = useLocale()
  const luxon = useLuxon()
  const Theme = ThemeWithoutType as ThemeType
  const {isLoading: globalIsLoading, setIsLoading: setGlobalIsLoading} = useBookingLoader()
  const navigate = useNavigate()
  const params = useParams<{venueId: string}>()
  const [searchParams] = useSearchParams()
  const {openPaymentForm, imageSrc, handleLinkCallback} = usePaymentProvider()
  const {data: venue, isLoading: isVenueLoading} = useQuery(
    VenueService.queries.getVenueQuery({params: {id: params.venueId}}),
  )
  const {data: currencyRate} = useQuery(
    VenueService.queries.getCurrencyRateQuery({params: {venueId: params.venueId}}),
  )
  const [disableAutoRedirect, setDisableAutoRedirect] = useState(false)
  const queryData = useMemo(() => extractDataFromQuery(searchParams), [searchParams])
  const posPromptpayQrShownRef = useRef(false)
  // Kept current every render so the Firestore listener can call the latest
  // handleLinkCallback without taking it as a dependency (it is not memoised).
  const handleLinkCallbackRef = useRef(handleLinkCallback)
  handleLinkCallbackRef.current = handleLinkCallback

  const {mutate: updatePosTerminalProcess} = useUpdatePosTerminalProcess()
  const navigateWhenNotActive = () => {
    if (isNotNilAndNotEmpty(queryData.posTerminalProcessId)) {
      if (queryData.withQR) {
        navigate(`/${params.venueId}`)
      } else {
        window.location.href = `${CHECKIN_URL}`
      }
    }
  }
  // Set once the process doc has delivered a charge carrying no QR at all.
  // Without it the wait below is unbounded: no QR ever arrives, so the return
  // timer stays disabled and the customer sits on a blank page indefinitely.
  const [posQrUnavailable, setPosQrUnavailable] = useState(false)
  const isWaitingForPromptpayQr = queryData.paymentMethod === 'promptpay'
    && !!queryData.posTerminalProcessId
    && !imageSrc
    && !posPromptpayQrShownRef.current
    && !posQrUnavailable

  const {goBackMessage} = useTimeout({
    callback: () => {
      navigateWhenNotActive()
      return null
    },
    delay: 10000,
    isDisabled: isNil(queryData.posTerminalProcessId) || disableAutoRedirect || isNotNilAndNotEmpty(imageSrc) || globalIsLoading
      || isNotNilAndNotEmpty(queryData.withQR) || isWaitingForPromptpayQr
  })

  const {error, draftEntity, draftEntityLoading, setDraftPendingStatus, setStartRefetch} = useHandleDraftEntityStatusChange({
    entity: queryData.entity,
    isLoading: globalIsLoading,
    entityId: queryData.entityId,
    setIsLoading: setGlobalIsLoading,
    posTerminalProcessId: queryData.posTerminalProcessId,
    redirectOnFail: true,
  })
  const isLoading = globalIsLoading || draftEntityLoading || isVenueLoading

  // Checkout gate (exsportia-product#80). Read on mount; the auto-open waits for this read (3 s
  // fail-open) so a deep link can't open the Opn widget before the status is known.
  const {data: terminal} = useQuery(
    PaymentService.queries.getTerminalQuery({params: {venueId: params.venueId}, refetchOnWindowFocus: false}),
  )
  const {data: providerStatus, isFetched: isProviderStatusRead} = useQuery(
    ProviderStatusQueries.getProviderStatusQuery({params: {venueId: params.venueId}, enabled: Boolean(params.venueId)}),
  )
  const isChargeGated = isOpnChargeGated({
    provider: resolveGateProvider(terminal),
    chargeState: getOpnChargeState(providerStatus),
    totalPrice: draftEntity?.totalPrice,
  })
  const checkoutGate = useMemo(
    () => ({isGated: isChargeGated, hasOtherMethod: false, isSelectionBlocked: isChargeGated}),
    [isChargeGated],
  )

  const autoTriggeredRef = useRef(false)
  const pendingStatusCalledRef = useRef(false)
  useEffect(() => {
    const processId = queryData.posTerminalProcessId
    // Structure, cleanup and deps are develop's (early return, a real
    // dependency instead of [], and the error callback the POS branch had
    // dropped — a Firestore listener with no error handler fails silently).
    // The promptpay block is the POS branch's, folded in.
    if (!processId) {
      return
    }
    const unsub = db.collection('posTerminalOperations').doc(processId).onSnapshot(doc => {
      if (doc.exists) {
        const data = doc.data()
        // handleLinkCallback is NOT memoised in the provider, so it cannot go in
        // the dependency array — that would tear down and re-open this listener
        // on every render. Read the current one through a ref instead.
        const linkCallback = handleLinkCallbackRef.current
        // image_uri is the server-inlined data URL; download_uri is Omise's own
        // public link, stored on the same doc. The server nulls ONLY image_uri
        // when its own fetch fails, so falling back to the raw link is what
        // makes the kiosk's "open on this screen" hand-off actually work — it
        // fires precisely when image_uri is null, and this page reads the same
        // document the kiosk just gave up on.
        const posQrSrc = data?.chargeData?.image_uri || data?.chargeData?.download_uri
        // The check-in app's QR link carries no paymentMethod param, so the URL
        // alone can't tell us this is a PromptPay process — but the process doc
        // we are already subscribed to can (`selectedPaymentMethod` is stamped
        // at creation). Falling back to it is what makes the CHI-scan hand-off
        // show the PromptPay QR instead of stranding the customer on a bare
        // Pay page. (WBA-4468 retest finding, 2026-08-13)
        const effectivePaymentMethod = queryData.paymentMethod || data?.selectedPaymentMethod
        if (effectivePaymentMethod === 'promptpay' && posQrSrc && linkCallback && !posPromptpayQrShownRef.current) {
          posPromptpayQrShownRef.current = true
          linkCallback({
            type: 'image',
            downloadUrl: posQrSrc,
            expires_at: data.chargeData.expires_at ?? null,
          })
          setGlobalIsLoading(true)
          setStartRefetch(true)
          if (data?.subStatus === POS_TERMINAL_STATUSES.IN_PROCESS) {
            updatePosTerminalProcess({id: processId, subStatus: POS_TERMINAL_STATUSES.USER_INPROCESS_WITH_QR, withQR: true})
          }
        }
        if (effectivePaymentMethod === 'promptpay' && isNotNilAndNotEmpty(data?.chargeData) && !posQrSrc) {
          setPosQrUnavailable(true)
        }
        if (queryData.withQR && data?.subStatus == POS_TERMINAL_STATUSES.IN_PROCESS && data.subStatus !== POS_TERMINAL_STATUSES.USER_INPROCESS_WITH_QR) {
          updatePosTerminalProcess({id: processId, subStatus: POS_TERMINAL_STATUSES.USER_INPROCESS_WITH_QR, withQR: true})
        }
        const isExpired = luxon.now().toUTC().valueOf() > data?.posTrackTimeLimit
        if (data?.status !== 'active' || (isExpired && [POS_TERMINAL_STATUSES.IN_PROCESS, POS_TERMINAL_STATUSES.USER_INPROCESS_WITH_QR].includes(data.subStatus as string))) {
          navigateWhenNotActive()
        }
      }
    }, err => {
      console.log(`Encountered error: ${err}`)
    })
    return () => {
      setPosPaymentInfoToSession(queryData)
      unsub()
    }
  }, [queryData.posTerminalProcessId])

  // Auto-open the provider form for a pre-selected NON-promptpay method (POS
  // branch). Promptpay is excluded: its QR arrives through the listener above.
  useEffect(() => {
    const method = queryData.paymentMethod
    if (
      method &&
      method !== 'promptpay' &&
      !autoTriggeredRef.current &&
      !draftEntity?.isPaid &&
      !draftEntity?.isCancelled &&
      !isLoading &&
      openPaymentForm &&
      draftEntity?.entityId &&
      draftEntity?.totalPrice &&
      isProviderStatusRead
    ) {
      // Gated: handled — the notice says why; no draft is moved to PAYMENT_PENDING, no widget, no
      // repeated toast. The customer can press Pay once the gate clears.
      if (isChargeGated) {
        autoTriggeredRef.current = true
        return
      }
      if (!pendingStatusCalledRef.current) {
        pendingStatusCalledRef.current = true
        setDisableAutoRedirect(true)
        setDraftPendingStatus()
      }
      const opened = openPaymentForm({
        entity: queryData.entity,
        bookingId: draftEntity?.entityId,
        totalPrice: draftEntity?.totalPrice,
        defaultPaymentMethodOverride: method,
        endProcessCallback: () => setDisableAutoRedirect(false),
        startProcessCallback: () => {
          setDisableAutoRedirect(false)
          setGlobalIsLoading(true)
        },
      })
      if (opened) {
        autoTriggeredRef.current = true
      }
    }
  }, [draftEntity, openPaymentForm, queryData.paymentMethod, queryData.entity, isLoading, isProviderStatusRead, isChargeGated])
  const memoizedGoBackMessage = useMemo(() => goBackMessage, [goBackMessage])

  const calculateGbp = useCallback((totalTHB: number | undefined, rate: number | undefined, decimals = 2) => {
    if (!totalTHB || !rate || totalTHB <= 0 || rate <= 0) {
      return null
    }
    return (totalTHB * rate).toFixed(decimals)
  }, [])

  // Check if rate is stale (>7 days)
  const isStale = useCallback((rateUpdatedAt: string | undefined) => {
    if (!rateUpdatedAt) {
      return true
    }
    const days = (Date.now() - new Date(rateUpdatedAt).getTime()) / (1000 * 60 * 60 * 24)
    return days > 7
  }, [])

  const gbpAmount = useMemo(() => {
    if (!currencyRate?.enabled || !currencyRate.thbToGbpRate || !draftEntity?.totalPrice) {
      return null
    }
    const decimals = typeof currencyRate.rounding?.decimals === 'number' ? currencyRate.rounding.decimals : 2
    const totalPrice = Number(draftEntity.totalPrice)
    const rate = typeof currencyRate.thbToGbpRate === 'number' ? currencyRate.thbToGbpRate : null
    if (!rate) {
      return null
    }
    return calculateGbp(totalPrice, rate, decimals)
  }, [currencyRate, draftEntity?.totalPrice, calculateGbp])

  const isRateStale = useMemo(() => {
    if (!currencyRate?.enabled || !currencyRate.rateUpdatedAt) {
      return false
    }
    const updatedAt = typeof currencyRate.rateUpdatedAt === 'string' ? currencyRate.rateUpdatedAt : undefined
    if (!updatedAt) {
      return false
    }
    return isStale(updatedAt)
  }, [currencyRate, isStale])

  const showGbp = useMemo(() => {
    if (!currencyRate?.enabled || !currencyRate.thbToGbpRate) {
      return false
    }
    const rate = typeof currencyRate.thbToGbpRate === 'number' ? currencyRate.thbToGbpRate : null
    return Boolean(rate && rate > 0 && gbpAmount !== null)
  }, [currencyRate, gbpAmount])
  const rateInfo = useMemo(() => {
    if (!currencyRate?.enabled || !currencyRate.rateInfo) {
      return null
    }
    return currencyRate.rateInfo || null
  }, [currencyRate, gbpAmount])
  const rateLabel = useMemo(() => {
    if (!currencyRate?.enabled || !currencyRate.rateLabel) {
      return null
    }
    return currencyRate.rateLabel
  }, [currencyRate])

  const handleClickGoToEntity = useCallback(() => {
    if (!queryData.entity || !queryData.entityId) {
      return
    }
    if (queryData.entity === 'booking-split' || queryData.entity === 'booking_update' || queryData.entity === 'booking') {
      const redirectPath = `/${params.venueId}/bookings/${queryData.entityId}`
      navigate(redirectPath)
    } else {
      const redirectPath = `/clear-view/${params.venueId}/payment?entityId=${queryData.entityId}&entity=${queryData.entity}`
      navigate(redirectPath)
    }
  }, [queryData, params.venueId, navigate])

  const handleTriggerPayment = useCallback(() => {
    if (isChargeGated) {
      return
    }
    setDisableAutoRedirect(true)
    setDraftPendingStatus()
    openPaymentForm &&
      openPaymentForm({
        entity: queryData.entity,
        bookingId: draftEntity?.entityId,
        totalPrice: draftEntity?.totalPrice,
        endProcessCallback: () => setDisableAutoRedirect(false),
        startProcessCallback: () => {
          setDisableAutoRedirect(false)
          setGlobalIsLoading(true)
        }
      })
  }, [openPaymentForm, draftEntity, queryData, setDraftPendingStatus, setGlobalIsLoading, isChargeGated])

  const showViewDetails = useMemo(() => {
    return draftEntity?.isPaid && (queryData.entity === 'booking-split' || queryData.entity === 'booking_update' || queryData.entity === 'booking')
  }, [draftEntity?.isPaid, queryData.entity])

  const submitButtonMarkup = useMemo(() => {
    const text = l('pay')
    if (draftEntity?.isCancelled === true || draftEntity?.isPaid || error?.response?.status === 404) {
      return (
        <Flex flexDirection='column' gap={Theme.spaces.multiplier}>
          {showViewDetails && queryData.entityId && queryData.entity && (
            <Button
              disabled={isLoading}
              onClick={handleClickGoToEntity}
              isLoading={isLoading || isVenueLoading}
            >
              {l('viewDetails')}
            </Button>
          )}
          <Button
            disabled={isLoading}
            onClick={() => navigate(`/${params.venueId}`)}
            isLoading={isLoading || isVenueLoading}
            type={showViewDetails ? 'secondary' : undefined}
          >
            {l('backToVenue')}
          </Button>
        </Flex>
      )
    }
    return (
      <>
        <CheckoutGateNotice gate={checkoutGate} />
        <Button
          disabled={isLoading || isWaitingForPromptpayQr || isChargeGated}
          onClick={() => handleTriggerPayment()}
          isLoading={isLoading || isVenueLoading || isWaitingForPromptpayQr}
        >
          {text}
        </Button>
      </>
    )
  }, [handleTriggerPayment, handleClickGoToEntity, isLoading, draftEntity, error, isVenueLoading, showViewDetails, queryData, Theme, isWaitingForPromptpayQr, isChargeGated, checkoutGate])
  const valuesMarkup = useMemo(() => {
    if (error?.response?.status === 404) {
      return (
        <Flex
          alignItems='center'
          gap={Theme.spaces.multiplier2x}
          flexDirection='column'
        >
          <Icon
            settings={{
              width: '120px',
              height: '120px',
              iconWidth: '80px',
              iconHeight: '80px',
              bg: Theme.colors.bgSecondary,
            }}
            styleType='withBg'
            icon={'smileSad'}
          />
          <Text
            settings={{textAlign: 'center'}}
            text={'Entity for payment not found'}
          />
        </Flex>
      )
    }

    return (
      <Flex
        flexDirection='column'
        gap={Theme.spaces.multiplier}
      >
        <Flex justifyContent='space-between'>
          <Text
            settings={{fontWeight: 'bold'}}
            text={l('totalPrice')}
          />
          {draftEntityLoading || isVenueLoading ? (
            <Flex width='40%'>
              <GradientLoader />
            </Flex>
          ) : (
            <Text
              settings={{fontWeight: 'bold'}}
              text={l(`${venue?.currency}_symbolic` as TranslatorScope, {
                value: Number(draftEntity?.totalPrice).toFixed(2),
              })}
            />
          )}
        </Flex>
        {showGbp && gbpAmount && (
          <Flex
            gap='8px'
            p='16px'
            borderRadius='12px'
            flexDirection='column'
            mt={Theme.spaces.multiplier}
            bg={Theme.colors.bgSecondary}
          >
            <Flex
              justifyContent='space-between'
              alignItems='center'
            >
              <Text
                settings={{fontSize: '14px', color: Theme.colors.text, fontWeight: 'bold'}}
                text={rateLabel || 'Your payment will be taken in Thai Baht which is the equivalent of:'}
              />
              <Flex
                alignItems='center'
                gap='4px'
              >
                <Text
                  settings={{fontWeight: 'bold', fontSize: '20px', color: '#07522c'}}
                  text={`~${currencyRate?.displayCurrency === 'GBP' ? '£' : currencyRate?.displayCurrency || '£'}${gbpAmount}`}
                />
              </Flex>
            </Flex>
          </Flex>
        )}
        {
          rateInfo && (
            <Info
              settings={{text: {croppedLines: 'unset'}}}
              text={rateInfo}
            />
          )
        }
      </Flex>
    )
  }, [draftEntity, isLoading, error, isVenueLoading, showGbp, gbpAmount, currencyRate, isRateStale, Theme, l])

  return (
    <Flex
      justifyContent='center'
      pt={Theme.spaces.multiplier3x}
    >
      <Box
        overflow='hidden'
        bg={Theme.colors.bg}
        maxWidth='600px'
        width='100%'
        borderRadius='24px'
        className='grid-layout_box_content grid-layout_box_content_padding'
      >
        <IconWithText
          title={venue?.name || l('venueName')}
          icon={
            <Image
              width={'64px'}
              height={'64px'}
              borderRadius='50%'
              src={getVenueLogoUrl(venue)}
            />
          }
          styleType='profileBig'
        />
        <Flex
          my='24px'
          gap='12px'
          flexDirection='column'
        >
          <Flex gap={Theme.spaces.multiplier}>
            <Title
              styleType='h3'
              text={l('labels_pricingAndPayments')}
            />
            {draftEntity?.isCancelled ? (
              <Tag
                settings={{bg: Theme.colors.error, iconWithText: {text: {color: Theme.colors.textContrast}}}}
                text={l('cancelled')}
              />
            ) : (
              draftEntity?.isPaid && (
                <Tag
                  settings={{bg: Theme.colors.success, iconWithText: {text: {color: Theme.colors.textContrast}}}}
                  text={l('paid')}
                />
              )
            )}
          </Flex>
          {draftEntity?.lines.map((el, index) => (
            <Flex
              justifyContent='space-between'
              key={`pricing-details-${index}`}
            >
              <Text
                text={el.text}
                settings={{maxWidth: 'calc(100% - 100px)'}}
              />
              <Text
                text={`${el.subtract ? '-' : ''}${l(`${venue?.currency}_symbolic` as TranslatorScope, {
                  value: Number(el.price).toFixed(2),
                })}`}
              />
            </Flex>
          ))}
          {valuesMarkup}
        </Flex>
        {submitButtonMarkup}
        {memoizedGoBackMessage}
      </Box>
    </Flex>
  )
}

export const PaymentPage: FC = (props) => (
  <PaymentProviderProvider>
    <PaymentPageComponent {...props} />
  </PaymentProviderProvider>
)
